import { STALE_BUNDLE_MESSAGE, STALE_BUNDLE_STATUS_TEXT } from '../../services/stale-bundle-messages';

/** The fields `beforeSend` reads off a thrown `HttpError` / `HttpErrorResponse`. */
export interface SentryHttpLike {
  status?: number;
  statusText?: string;
  url?: string | null;
}

/**
 * Whether a caught HTTP failure should reach Sentry.
 *
 * 🔴 Extracted from `main.ts` so it can be tested. Bootstrap code is the one
 * place nothing runs a spec over, and this decides what we can SEE in
 * production — a rule that silently stops matching takes the alarm with it, and
 * nothing fails.
 *
 * ⚠️ ONLY SEES ERRORS THAT CARRY A `status`, and MOST `/app/*` ones do not.
 *
 * `ApiV2Service.errorHandle` ends `throwError(() => errorMessage)` — a plain
 * STRING (`ui/CLAUDE.md` says so too), so those arrive with no `status` and fall
 * straight through untouched.
 *
 * 🔴 But NOT all of them, and an earlier version of this comment said "every
 * NestJS call", which is wrong. Two `/app/*` paths bypass `errorHandle` and
 * propagate the raw `HttpError` with `status` and `url` intact: `upload()` /
 * `postMultipart()`, which have no `catchError` at all, and any `put(...,
 * { preserveErrorResponse: true })`. So an evidence upload by a locked-out
 * account does reach here as a real 402.
 *
 * The legacy .NET path also arrives intact — `ApiCallService.errorHandle` ends
 * `throwError(() => error)` with the `HttpError` — so the 401/403 rules below
 * are live, not decoration.
 *
 * A 402 rule was tried here and removed. Not because it "could never fire", but
 * because its premise was never measured (Sentry's `ErrorHandler` sees only
 * UNHANDLED errors, and most 402s are caught and rendered inline) and its
 * billing-vs-product URL split was wrong in both directions — it covered three of
 * the eleven `@BillingExempt()` route groups (nine class-level plus two
 * method-level in `account-writes.controller.ts`) while over-matching the
 * undecorated routes under `/app/accounts/`. An unhandled 402 is worth seeing, so it is now
 * simply kept.
 */
export function shouldReportToSentry(err: SentryHttpLike | undefined): boolean {
  // WAM-602 review round 3 #2 — `ApiV2Service.errorHandle` collapses MOST
  // `/app/*` failures to a plain STRING (see the doc above), including a
  // stale-bundle refusal: `extractServerMessage()` reads `error.error.message`
  // off the interceptor's synthetic body and rethrows exactly that string, so
  // it arrives here as `STALE_BUNDLE_MESSAGE` itself rather than anything
  // carrying `status`/`statusText`. The object-shaped rule below (matched on
  // `statusText`) cannot see this at all — this is a second, string-shaped
  // check for the SAME refusal.
  if ((err as unknown) === STALE_BUNDLE_MESSAGE) return false;

  const status = err?.status;

  // 401 and 403 are the server correctly refusing a request, not the app
  // breaking. Pre-existing behaviour, kept.
  if (status === 401 || status === 403) return false;

  // WAM-602 review round 2 #5 — a write refused because THIS TAB is running
  // a stale bundle is expected, and already visible to the user via the
  // banner/modal; it is not "the app broke". Plenty of call sites
  // `.subscribe()` a write with no error handler, which otherwise turns
  // every refused write into an "unhandled error" flood the moment a
  // mismatch is confirmed. Matched on `statusText`, not `status === 409`
  // alone: `staleBundleWriteGuardInterceptor` is the only thing that sets
  // this exact statusText, whereas 409 is also a legitimate SERVER-side
  // conflict elsewhere (e.g. Stripe's idempotency-key reuse) that should
  // still be reported.
  if (status === 409 && err?.statusText === STALE_BUNDLE_STATUS_TEXT) return false;

  return true;
}
